import { track } from '@amplitude/analytics-browser';
import { formatDate } from '@angular/common';
import { Component } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from '../../auth/session/session.service';
import { Setting, SettingsService, SettingsUpdateRequest } from '../../core/settings/settings.service';
import { BtnReviewRequestComponent } from '../button-review-request/button-review-request.component';
import { SettingsRequestCreationModalComponent } from '../request-creation-modal/request-creation-modal.component';
import { SettingsStatusComponent } from '../settings-status/settings-status.component';
import { SettingRequestViewRendererComponent } from '../view-request-renderer/view-request-renderer.component';

@Component({
  templateUrl: './settings-requests.component.html',
  styleUrls: ['./settings-requests.component.scss']
})
export class SettingsRequestsComponent {
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true,
    suppressMovable: true
  };
  pageSize = 50;
  pageSizes: number[] = [25, 50, 75, 100];
  paginationDateMap: Map<number, Date> = new Map();
  firstPageDate: Date;
  currentPage = 1;
  endOfSet: boolean;
  query: {};

  private gridApi!: GridApi<SettingsUpdateRequest>;
  private settingsUpdateRequests: SettingsUpdateRequest[] = [];
  private userName = this.sessionService.getCurrentSession().name;

  constructor(
    private readonly settingService: SettingsService,
    private readonly toastr: ToastrService,
    private readonly modalService: NgbModal,
    private readonly sessionService: SessionService
  ) { }

  ngOnInit(): void {
    track('Control Center Device Settings page Loaded');
    this.columnDefs = [
      {
        headerName: 'Request ID',
        field: 'id',
        cellRenderer: SettingRequestViewRendererComponent
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: SettingsStatusComponent
      },
      {
        cellRenderer: BtnReviewRequestComponent,
        cellRendererParams: {
          userName: this.userName
        }
      },
      {
        headerName: 'Settings',
        field: 'settings',
        cellRenderer: (params: any) => {
          return params.value.map((setting: Setting) => this.settingService.getSettingType(setting.name).label).join(', ');
        }
      },
      {
        headerName: 'Request Date',
        field: 'requestDate',
        cellRenderer: (params: any): string => formatDate(params.value, 'short', 'en-US')
      },
      {
        headerName: 'Requester Name',
        field: 'requester'
      },
      {
        headerName: 'Request Description',
        field: 'description'
      },
      {
        headerName: 'Reviewer Name',
        valueGetter: this.getReviewerName.bind(this)
      },
      {
        headerName: 'Review Date',
        valueGetter: this.getReviewDate.bind(this)
      },
      {
        headerName: 'Review Description',
        valueGetter: this.getReviewDescription.bind(this)
      }
    ];
  }

  async onGridReady(params: GridReadyEvent): Promise<void> {
    this.gridApi = params.api;

    await this.queryData({}, true);

    window.addEventListener('resize', () => {
      setTimeout(() => {
        params.api.sizeColumnsToFit();
      });
    });
  }

  onRefresh(): Promise<void> {
    const resetPagination = this.currentPage === 1;
    return this.queryData(this.query, resetPagination);
  }

  async queryData(query, resetPagination: boolean): Promise<void> {
    this.gridApi.showLoadingOverlay();
    if (resetPagination) {
      this.resetPaginationDateMap();
    }
    await this.getData(query);
    this.gridApi.hideOverlay();
    this.gridApi.sizeColumnsToFit();
  }

  async openNewModificationRequestModal(): Promise<void> {
    const modal = this.modalService.open(SettingsRequestCreationModalComponent, { size: 'lg', windowClass: "lytxModal", centered: true });
    modal.componentInstance.requesterName = this.userName;

    try {
      await modal.result;
    } catch (e) {
      console.error(e);
    }

    await this.onRefresh();
  }

  async onPageSizeSelected(pageSize: number): Promise<void> {
    this.pageSize = pageSize;
    this.addURLParam('pageSize', '' + pageSize);
    await this.queryData(this.query, true);
  }

  async onPageSelection(pageNumber: number): Promise<void> {
    if (this.currentPage === pageNumber) {
      return;
    }
    this.currentPage = pageNumber;
    this.addURLParam('page', '' + pageNumber);
    await this.queryData(this.query, false);
  }

  private async getData(query): Promise<void> {
    this.query = query;
    const date = new Date(this.paginationDateMap.get(this.currentPage));
    query['requestDateEnd'] = date.toISOString();
    query['pageSize'] = this.pageSize;
    try {
      this.settingsUpdateRequests = await this.settingService.getSettingsUpdateRequests(query);
    } catch (error) {
      this.gridApi.setRowData([]);
      this.toastr.error('Failure retrieving data.');
    }

    this.updatePageSettings();
    this.gridApi.setRowData(this.settingsUpdateRequests);
  }

  private getReviewerName(params: any): string {
    const reviews = params.data.reviews;
    return reviews && reviews.length > 0 ? reviews[0].reviewer : '';
  }

  private getReviewDate(params: any): string {
    const reviews = params.data.reviews;
    return reviews && reviews.length > 0 ? formatDate(reviews[0].reviewDate, 'short', 'en-US') : '';
  }

  private getReviewDescription(params: any): string {
    const reviews = params.data.reviews;
    return reviews && reviews.length > 0 ? reviews[0].description : '';
  }

  private updatePageSettings(): void {
    this.endOfSet = this.settingsUpdateRequests.length < this.pageSize;
    if (!this.endOfSet) {
      this.paginationDateMap.set(this.currentPage + 1, this.settingsUpdateRequests[this.pageSize - 1].requestDate);
    }
  }

  private resetPaginationDateMap(): void {
    this.paginationDateMap.clear();
    this.firstPageDate = new Date();
    this.paginationDateMap.set(1, this.firstPageDate);
    this.currentPage = 1;
  }

  private addURLParam(key: string, value: string) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState(null, null, url);
  }
}
